import {
  DisabledWrapper,
  flexibilityExpiredTooltipCopy,
} from "#clockwise/web-commons/src/ui/disabled-wrapper";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { Switch } from "@clockwise/design-system";
import { fg_warning } from "@clockwise/design-system/tokens";
import { Tooltip } from "@material-ui/core";
import { Autorenew, ErrorOutline } from "@material-ui/icons";
import Skeleton from "@material-ui/lab/Skeleton";
import classNames from "classnames";
import { noop } from "lodash";
import { CalendarBlank, Sparkle, Warning } from "phosphor-react";
import React, { ReactElement } from "react";

type EventCardProps = {
  conflicted?: boolean;
  editable?: boolean;
  errorMessage?: string;
  eventId?: string;
  flexible?: boolean;
  loading?: boolean;
  loadingDetails?: boolean;
  onFlexibilityChange?: (eventId: string, isFlexible: boolean) => void;
  recurrence?: string;
  restricted?: boolean;
  title?: string;
  updating?: boolean;
};

export const isEventCard = (
  component: React.ReactNode,
): component is ReactElement<EventCardProps> => {
  if (
    !component ||
    !React.isValidElement(component) ||
    typeof component.type === "string" ||
    typeof component.type === "number" ||
    component.type.name !== EventCard.name
  ) {
    return false;
  }

  return true;
};

const EventCard = ({
  conflicted = false,
  editable = true,
  errorMessage,
  eventId = "",
  flexible = false,
  loading = false,
  loadingDetails = false,
  onFlexibilityChange = noop,
  recurrence = "",
  restricted = false,
  title = "Untitled Meeting",
  updating = false,
}: EventCardProps) => {
  const restrictedAndFlexible = restricted && flexible;
  const restrictedAndDisabled = restricted && !flexible;
  const showWarningUIState = restrictedAndFlexible || conflicted;

  const translation = React.useContext(TranslationContext);
  const tooltipText = translation("autopilot-form:toggle-tooltip", { on: !!flexible });

  const handleFlexibilityChange = (isFlexible: boolean) => onFlexibilityChange(eventId, isFlexible);

  let icon = <Sparkle size={18} />;
  if (!flexible) icon = <CalendarBlank size={18} />;
  if (showWarningUIState) icon = <Warning size={18} color={fg_warning} />;

  let borderStyle = "cw-border-default";
  if (errorMessage) borderStyle = "cw-border-warning-emphasis";
  else if (showWarningUIState) borderStyle = "cw-border-warning";
  else if (flexible) borderStyle = "cw-border-brand";

  return (
    <DisabledWrapper
      isDisabled={restrictedAndDisabled}
      tooltipBody={flexibilityExpiredTooltipCopy.header}
    >
      <div className="cw-bg-default cw-grow cw-min-w-[200px] cw-body-base">
        <div
          className={`cw-border cw-border-1 cw-border-solid cw-rounded-md cw-h-fit cw-min-h-0 ${borderStyle}`}
        >
          <div className="cw-px-3 cw-py-2 cw-block">
            <>
              <div className="cw-p-0 cw-flex cw-items-center cw-justify-start">
                <span className={classNames("cw-text-lg cw-mr-2 cw-flex-grow-0")}>
                  {loading && <Skeleton variant="circle" width={20} height={20} />}
                  {!loading && icon}
                </span>
                <span
                  className={classNames("cw-heading-sm cw-flex-grow", {
                    "cw-text-warning": showWarningUIState,
                  })}
                >
                  {loading && <Skeleton variant="text" width="80%" />}
                  {!loading && title}
                </span>
                {editable && (
                  <Tooltip title={tooltipText}>
                    <span>
                      <Switch
                        checked={flexible}
                        disabled={loading || updating || restrictedAndDisabled}
                        onChange={(isFlexible) => handleFlexibilityChange(isFlexible)}
                        sentiment={showWarningUIState ? "warning" : "neutral"}
                        size="small"
                        cw-id="event-card-flexibility-toggle"
                      />
                    </span>
                  </Tooltip>
                )}
              </div>
              {errorMessage && (
                <div className="cw-flex cw-items-center cw-py-1 cw-text-xs cw-text-warning">
                  <ErrorOutline className="cw-h-4 cw-mr-2 cw-flex-shrink-0 cw-text-warning" />
                  <div className="cw-text-warning">{errorMessage}</div>
                </div>
              )}
              <div
                className={classNames("cw-flex cw-items-center cw-py-1 cw-text-xs", {
                  "cw-text-warning": showWarningUIState && !loadingDetails,
                  "cw-text-positive": !showWarningUIState && !loadingDetails && flexible,
                })}
              >
                {" "}
                {(loading || loadingDetails || recurrence) && (
                  <>
                    {loading ||
                      (loadingDetails && (
                        <Skeleton
                          className="cw-h-4 cw-mr-3 cw-flex-shrink-0"
                          variant="circle"
                          width={16}
                          height={16}
                        />
                      ))}

                    {!loading && !loadingDetails && !restrictedAndFlexible && (
                      <Autorenew className="cw-h-4 cw-mr-1 cw-flex-shrink-0" />
                    )}
                    {(loading || loadingDetails) && (
                      <div className="cw-w-full">
                        <Skeleton variant="text" width="100%" />
                        <Skeleton variant="text" width="40%" />
                      </div>
                    )}
                    {!loading && !loadingDetails && !restrictedAndFlexible && (
                      <div className="">{recurrence}</div>
                    )}
                    {!loading && !loadingDetails && restrictedAndFlexible && (
                      <div className="cw-flex cw-items-center cw-py-1 cw-text-xs cw-text-warning">
                        Please upgrade so that Clockwise can move your flexible meetings
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          </div>
        </div>
      </div>
    </DisabledWrapper>
  );
};

export default EventCard;
